import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'motion/react';
import { Cookie, Shield, ShieldCheck } from '@/components/ui/icon';
import { Capacitor } from '@capacitor/core';
import { Button } from '@/components/ui/button';
import { getConsent, setConsent, type ConsentChoice } from '@/lib/consent';
import { NATIVE_SMOKE_ROUTE_KEY } from '@/lib/native-app-routes';
import { initializeConsentedSentry } from '@/lib/sentry-loader';

interface CookieConsentBannerProps {
  onDecision?: (choice: ConsentChoice) => void;
}

/**
 * Fixed-position consent banner shown on first visit.
 *
 * It is intentionally lightweight (no Radix / no portal) so it renders before
 * heavier layout code and cannot be blocked by an error boundary upstream.
 */
export function CookieConsentBanner({ onDecision }: CookieConsentBannerProps) {
  const { t } = useTranslation();
  const bannerRef = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const shouldReduceMotion = useReducedMotion();

  useEffect(() => {
    // Run after mount so SSR-ish static HTML never flashes the banner.
    if (Capacitor.isNativePlatform() && window.localStorage.getItem(NATIVE_SMOKE_ROUTE_KEY)) {
      setConsent('essential');
      return;
    }

    const record = getConsent();
    if (!record) setVisible(true);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    if (visible) {
      document.documentElement.dataset.cookieConsentVisible = 'true';
      const publishHeight = () => {
        const height = Math.ceil(bannerRef.current?.getBoundingClientRect().height ?? 0);
        document.documentElement.style.setProperty('--app-cookie-banner-height', `${height}px`);
      };

      let frameId = window.requestAnimationFrame(publishHeight);
      const scheduleMeasure = () => {
        window.cancelAnimationFrame(frameId);
        frameId = window.requestAnimationFrame(publishHeight);
      };

      window.addEventListener('resize', scheduleMeasure, { passive: true });
      const observer =
        typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(scheduleMeasure);
      if (bannerRef.current) observer?.observe(bannerRef.current);

      return () => {
        window.cancelAnimationFrame(frameId);
        window.removeEventListener('resize', scheduleMeasure);
        observer?.disconnect();
        delete document.documentElement.dataset.cookieConsentVisible;
        document.documentElement.style.removeProperty('--app-cookie-banner-height');
      };
    }

    delete document.documentElement.dataset.cookieConsentVisible;
    document.documentElement.style.removeProperty('--app-cookie-banner-height');
  }, [visible]);

  const decide = useCallback(
    (choice: ConsentChoice) => {
      if (exiting) return;

      setConsent(choice);
      if (choice === 'all') {
        void initializeConsentedSentry();
      }
      setExiting(true);
      onDecision?.(choice);
    },
    [exiting, onDecision],
  );

  if (!visible) return null;

  return (
    <motion.div
      ref={bannerRef}
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-description"
      data-testid="cookie-consent-banner"
      initial={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: '100%' }}
      animate={{
        opacity: exiting ? 0 : 1,
        ...(shouldReduceMotion ? {} : { y: exiting ? '100%' : 0 }),
        transition: {
          duration: shouldReduceMotion ? 0 : exiting ? 0.16 : 0.24,
          ease: exiting ? [0.4, 0, 1, 1] : [0.22, 1, 0.36, 1],
        },
      }}
      onAnimationComplete={() => {
        if (exiting) setVisible(false);
      }}
      className="cookie-consent-bar"
      style={{ bottom: 'var(--app-cookie-banner-bottom)' }}
    >
      <div className="cookie-consent-inner">
        <div className="cookie-consent-notice">
          <Cookie className="cookie-consent-icon" aria-hidden="true" />
          <div className="min-w-0">
            <h2 id="cookie-consent-title" className="cookie-consent-title">
              {t('cookieConsent.title', 'We value your privacy')}
            </h2>
            {/* Always rendered so aria-describedby stays valid and assistive tech
                reads the full notice even while it is visually collapsed. */}
            <p
              id="cookie-consent-description"
              className={detailsOpen ? 'cookie-consent-description' : 'sr-only'}
            >
              {t(
                'cookieConsent.description',
                'We use essential cookies to keep you signed in and to operate core features. With your permission we also use analytics to improve the product. You can change this anytime in your profile settings.',
              )}
            </p>
            <div className="cookie-consent-links">
              <button
                type="button"
                className="cookie-consent-link"
                aria-expanded={detailsOpen}
                aria-controls="cookie-consent-description"
                onClick={() => setDetailsOpen((open) => !open)}
              >
                {t('cookieConsent.details', 'Details')}
              </button>
              <Link to="/cookie-policy" className="cookie-consent-link">
                {t('cookieConsent.policyLink', 'Read our Cookie Policy')}
              </Link>
            </div>
          </div>
        </div>

        <div className="cookie-consent-actions">
          <Button
            variant="ghost"
            className="cookie-consent-action"
            onClick={() => decide('essential')}
            data-testid="cookie-consent-essential"
            disabled={exiting}
          >
            <Shield className="size-4 shrink-0" aria-hidden="true" />
            {t('cookieConsent.essentialOnly', 'Essential only')}
          </Button>
          <Button
            variant="default"
            className="cookie-consent-action"
            onClick={() => decide('all')}
            data-testid="cookie-consent-accept-all"
            disabled={exiting}
          >
            <ShieldCheck className="size-4 shrink-0" aria-hidden="true" />
            {t('cookieConsent.acceptAll', 'Accept all')}
          </Button>
        </div>
      </div>
    </motion.div>
  );
}
